'use client';

import { Dialog } from '@base-ui/react/dialog';
import { css } from '@repo/pandacss/styled-system/css';
import { ReactNode, startTransition, useCallback } from 'react';
import { useShallow } from 'zustand/shallow';
import { useFilterEvents } from '../useFilterEvents';
import { useScrollLockOnMobile } from '../useScrollLockOnMobile';
import type { MobileDialogStoreProps } from './types';

export type { MobileDialogStoreProps };

type Props<TFilterState, TSortValue> = {
  isDialogOpen: boolean;
  handleOpenCloseDialog: () => void;
  dialogId: string;
  children: ReactNode;
  useStore: MobileDialogStoreProps<TFilterState, TSortValue>;
  filterPillsToDisplay: number;
  actionSection: string;
  pageIdentifier: string;
  onLocationReset?: () => void;
};

const backdropClass = css({
  position: 'fixed',
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
  backgroundColor: '[rgba(0,0,0,0.5)]',
  zIndex: '[1300]',
  '&[data-starting-style]': { opacity: '0' },
  '&[data-ending-style]': { opacity: '0' },
});

const popupClass = css({
  position: 'fixed',
  top: '0',
  right: '0',
  bottom: '0',
  left: '0',
  display: 'flex',
  flexDirection: 'column',
  backgroundColor: 'white',
  zIndex: '[1301]',
  outlineStyle: 'none',
  '&[data-starting-style]': { transform: '[translateY(100%)]' },
  '&[data-ending-style]': { transform: '[translateY(100%)]' },
  transitionProperty: '[transform]',
  transitionDuration: '[0.2s]',
  overflowY: 'auto',
});

const headerClass = css({
  paddingInlineStart: '4',
  paddingInlineEnd: '4',
  paddingTop: '[22px]',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  flexShrink: '0',
});

const titleClass = css({ fontSize: 'md', fontWeight: 'semibold', color: 'neutral.dark' });

const headerActionsClass = css({
  display: 'flex',
  alignItems: 'center',
  columnGap: '2',
  rowGap: '2',
});

const clearButtonClass = css({
  fontSize: 'xs',
  fontWeight: 'medium',
  color: 'neutral.dark',
  backgroundColor: 'neutral.lighter2',
  borderTopLeftRadius: 'lg',
  borderTopRightRadius: 'lg',
  borderBottomRightRadius: 'lg',
  borderBottomLeftRadius: 'lg',
  letterSpacing: '[0.03rem]',
  paddingInlineStart: '3',
  paddingInlineEnd: '3',
  paddingBlockStart: '2',
  paddingBlockEnd: '2',
  cursor: 'pointer',
  fontFamily: '[inherit]',
  _disabled: { opacity: '0.4', cursor: 'not-allowed' },
});

const closeButtonClass = css({
  backgroundImage: '[none]',
  backgroundColor: 'neutral.white',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  cursor: 'pointer',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  height: '[34px]',
  paddingTop: '1',
  paddingRight: '1',
  paddingBottom: '1',
  paddingLeft: '1',
  color: 'neutral.dark',
  fontSize: 'xl',
  fontFamily: '[inherit]',
  _focusVisible: {
    outlineStyle: 'none',
  },
  width: '[34px]',
});

const footerClass = css({
  position: 'fixed',
  left: '0',
  right: '0',
  bottom: '0',
  paddingTop: '4',
  paddingRight: '4',
  paddingBottom: '4',
  paddingLeft: '4',
  backgroundColor: 'white',
  flexShrink: '0',
});

const applyButtonClass = css({
  width: 'full',
  paddingTop: '4',
  paddingRight: '4',
  paddingBottom: '4',
  paddingLeft: '4',
  backgroundColor: 'green.main',
  color: 'white',
  borderTopStyle: 'none',
  borderRightStyle: 'none',
  borderBottomStyle: 'none',
  borderLeftStyle: 'none',
  borderTopLeftRadius: '1',
  borderTopRightRadius: '1',
  borderBottomRightRadius: '1',
  borderBottomLeftRadius: '1',
  fontSize: '[13px]',
  fontWeight: 'bold',
  lineHeight: 'md',
  cursor: 'pointer',
  fontFamily: '[inherit]',
  _hover: { backgroundColor: 'green.dark' },
  _focusVisible: {
    outlineWidth: 'medium',
    outlineStyle: 'solid',
    outlineColor: '[#0069B9]',
  },
});

export const MobileDialog = <TFilterState, TSortValue>({
  isDialogOpen,
  handleOpenCloseDialog,
  dialogId,
  children,
  useStore,
  filterPillsToDisplay,
  actionSection,
  pageIdentifier,
  onLocationReset,
}: Props<TFilterState, TSortValue>) => {
  const { clearAllFiltersEvent } = useFilterEvents({ actionSection, pageIdentifier });
  const { handleClearFilters, sortBy } = useStore(
    useShallow(state => ({ handleClearFilters: state.handleClearFilters, sortBy: state.sortBy })),
  );

  useScrollLockOnMobile(isDialogOpen);

  const handleClearAllFilters = useCallback(() => {
    setTimeout(() => {
      startTransition(() => {
        handleClearFilters();
        if (onLocationReset) onLocationReset();
        clearAllFiltersEvent();
      });
    }, 100);
  }, [handleClearFilters, onLocationReset, clearAllFiltersEvent]);

  return (
    <Dialog.Root
      open={isDialogOpen}
      onOpenChange={open => !open && handleOpenCloseDialog()}
      modal="trap-focus"
    >
      <Dialog.Portal>
        <Dialog.Backdrop className={backdropClass} />
        <Dialog.Popup className={popupClass} aria-labelledby={dialogId}>
          <div className={headerClass}>
            <Dialog.Title id={dialogId} className={titleClass}>
              FILTERS &amp; SORT
            </Dialog.Title>
            <div className={headerActionsClass}>
              <button
                type="button"
                className={clearButtonClass}
                onClick={handleClearAllFilters}
                disabled={!filterPillsToDisplay && !sortBy}
              >
                Clear All
              </button>
              <Dialog.Close className={closeButtonClass} aria-label="close dialog">
                ✕
              </Dialog.Close>
            </div>
          </div>

          {children}

          <div className={footerClass}>
            <button type="button" className={applyButtonClass} onClick={handleOpenCloseDialog}>
              APPLY
            </button>
          </div>
        </Dialog.Popup>
      </Dialog.Portal>
    </Dialog.Root>
  );
};
